// server/browser/src/lib/admin.ts
//
// Typed client for the admin console API. Every endpoint lives under
// /v1/admin/* (single login: the SPA's PKCE bearer authorizes it; the cloud
// re-checks the admin group claim on each call). Shapes mirror cloud/stats.py
// (collect / check_status), main.py (_identity_capabilities) and
// admin_billing.py.

import { request } from "./api";

// ---- status / health ---------------------------------------------------

export interface SystemProbe {
  url: string;
  online: boolean;
  latency_ms: number | null;
}

export interface AdminConfig {
  public_url: string;
  use_jwt_auth: boolean;
  rl_enabled: boolean;
  log_path: string;
  oidc_provider: string;
  jwt_audience: string;
  default_tier: string;
  api_keys_count: number;
  model_pricing_count: number;
  feedback_enabled: boolean;
  remote_control: boolean;
  pwa_url: string;
}

export interface AdminStatus {
  checked_at: string;
  systems: Record<string, SystemProbe>;
  mcp_backends: Record<string, SystemProbe>;
  feedback: { enabled?: boolean; backend?: string; online?: boolean } & Record<string, unknown>;
  config: AdminConfig;
  groups: {
    access_groups: string[];
    admin_group: string;
    tier_groups: Record<string, string[]>;
  };
}

export interface IdentityCapabilities {
  provider: string;
  issuer: string;
  oidc_discovery: boolean;
  jwks: boolean;
  token_exchange: string;
  kerberos_spnego: boolean;
}

// ---- live usage ---------------------------------------------------------

export interface UsageRow {
  sub: string;
  tier: string;
  rpm_used: number;
  rpm_limit: number;
  rpm_remaining: number;
  conc_current: number;
  conc_limit: number;
  tpd_used: number;
  tpd_limit: number;
  tpd_remaining: number;
}

export interface AdminStats {
  generated_at: string;
  bucket: string;
  users: UsageRow[];
  totals: { active_users: number; total_rpm: number; total_tokens_today: number };
  error?: string;
}

// ---- historical spend (by-app) -----------------------------------------

export interface ByAppRow {
  caller: string;
  kind: "app" | "user";
  requests: number;
  tokens_total: number;
  tokens_by_model: Record<string, number>;
  cost_usd: number;
}

export interface ByAppResult {
  since: string;
  until: string;
  rows: ByAppRow[];
  totals: { requests: number; tokens: number; cost_usd: number };
  unknown_models: string[];
}

// ---- billing ------------------------------------------------------------

export interface BillingUser {
  sub: string;
  email: string;
  name: string;
  status: string;
}

export interface UserDetail {
  sub: string;
  email: string;
  name: string;
  effective_plan: string;
  source: string;
  subscriptions: Array<{ id: string; plan_id: string; status: string; source: string }>;
  manual_grants: Array<{ id: string; plan_id: string; status: string; granted_by: string }>;
}

export interface Voucher {
  id: string;
  code: string;
  description: string;
  plan_id: string;
  duration_days: number;
  max_redemptions: number | null;
  redeemed_count: number;
  status: string;
  allowed_emails: string[];
  allowed_domains: string[];
  valid_until: string | null;
}

export interface Redemption {
  user_sub: string;
  email: string;
  plan_id: string;
  expires_at: string | null;
  status: string;
}

export interface WebhookError {
  provider_event_id: string;
  event_type: string;
  error: string;
  created_at: string | null;
}

export interface NewVoucher {
  code: string;
  plan_id: "pro" | "max";
  duration_days: number;
  description?: string;
  max_redemptions?: number | null;
}

export const admin = {
  status: () => request<AdminStatus>("/v1/admin/status"),
  stats: () => request<AdminStats>("/v1/admin/stats"),
  identity: () => request<IdentityCapabilities>("/v1/admin/identity/capabilities"),

  usageFiles: () => request<{ files: string[] }>("/v1/admin/usage"),
  usageByApp: (since: string, until: string) =>
    request<ByAppResult>("/v1/admin/usage/by-app", { query: { since, until } }),

  users: (email?: string) =>
    request<{ users: BillingUser[] }>("/v1/admin/billing/users", { query: { email } }),
  user: (sub: string) => request<UserDetail>(`/v1/admin/billing/users/${encodeURIComponent(sub)}`),
  grant: (sub: string, body: { plan: "pro" | "max"; reason?: string; expires_at?: string | null }) =>
    request(`/v1/admin/billing/users/${encodeURIComponent(sub)}/grant`, { method: "POST", body }),
  revoke: (grantId: string) =>
    request(`/v1/admin/billing/grants/${encodeURIComponent(grantId)}`, { method: "DELETE" }),

  vouchers: () => request<{ vouchers: Voucher[] }>("/v1/admin/billing/vouchers"),
  createVoucher: (body: NewVoucher) =>
    request<Voucher>("/v1/admin/billing/vouchers", { method: "POST", body }),
  disableVoucher: (id: string) =>
    request<Voucher>(`/v1/admin/billing/vouchers/${encodeURIComponent(id)}`, { method: "PATCH" }),
  redemptions: (id: string) =>
    request<{ redemptions: Redemption[] }>(
      `/v1/admin/billing/vouchers/${encodeURIComponent(id)}/redemptions`,
    ),

  webhookErrors: () => request<{ errors: WebhookError[] }>("/v1/admin/billing/webhook-errors"),
};

// ---- cosmetic admin-link gate -------------------------------------------

// Whether to surface the "Admin" shortcut in the site header. This is purely
// cosmetic — real authorization is enforced server-side on every /v1/admin/*
// call. We resolve it by probing the lightest admin endpoint (the same one
// AdminShell gates on); a non-admin gets 403 → false, and any other failure
// also yields false so we never paint a dead link. The verdict promise is
// cached for the session and deduped across concurrent callers.
let adminVerdict: Promise<boolean> | null = null;

export function isAdmin(): Promise<boolean> {
  if (!adminVerdict) {
    adminVerdict = admin
      .identity()
      .then(() => true)
      .catch(() => false);
  }
  return adminVerdict;
}

/** Drop the cached verdict so the next probe is fresh — call on sign-in /
 *  sign-out, when the token (and thus the group claim) may have changed. */
export function resetAdminGate(): void {
  adminVerdict = null;
}

/** Download a usage log file (auth via bearer; opens a blob save). */
export async function downloadUsageFile(filename: string): Promise<void> {
  const { getAccessToken } = await import("./auth");
  const token = getAccessToken();
  const res = await fetch(`/v1/admin/usage/${encodeURIComponent(filename)}`, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
